import React from "react";
import ButtonPill from "../../../../Buttons/ButtonPill";
import EmailFreeView from "./EmailFreeView";
import { RoutePaths } from "../../../../../Constants/RoutePaths";
import { VideoPlaybackState } from "../../../../../Hooks/Content/Watch/VideoSingle/useVideoPlayback";
import useFetchSubscriptionPlans from "../../../../../Hooks/Plans/useFetchSubscriptionPlans";
import { getAcquisitionPriceSentence, getResolvedOffer } from "../../../../../Helpers/BillingOffers";
import { withPromotionCode } from "../../../../../Helpers/PromotionCode";

interface VideoAccessOverlayProps {
    playback: VideoPlaybackState;
    needsFreeViewEmail: boolean;
    onFreeViewComplete: () => void;
    onRetry: () => void;
}

const VideoAccessOverlay: React.FC<VideoAccessOverlayProps> = ({
    playback, needsFreeViewEmail, onFreeViewComplete, onRetry,
}) => {
    const { plans } = useFetchSubscriptionPlans();
    const monthlyPlan = plans?.find(plan => plan.period === 0);
    const monthlyOffer = getResolvedOffer(monthlyPlan);
    const monthlyPrice = monthlyPlan ? getAcquisitionPriceSentence(monthlyPlan) : null;
    let content: React.ReactNode;
    if (playback.status === "loading") {
        content = <p role="status">Checking video access…</p>;
    } else if (playback.status === "error") {
        content = <><h2>Video access unavailable</h2><p role="alert">{playback.error}</p>
            <ButtonPill label="Try again" onClick={onRetry} />
            <ButtonPill label="Sign in" link={RoutePaths.Login} /></>;
    } else if (playback.decision.allowed) {
        if (!needsFreeViewEmail) return null;
        content = <><h2>This Video is Free!</h2><p>Get instant access using your email address:</p>
            <EmailFreeView onAccessGranted={onFreeViewComplete} /></>;
    } else {
        const decision = playback.decision;
        switch (decision.reason) {
            case "not_released": {
                const date = decision.available_at ? new Date(decision.available_at) : null;
                content = <><h2>Coming Soon</h2><p>{date && Number.isFinite(date.getTime())
                    ? `This video will be available on ${date.toLocaleString()}.`
                    : "This video is not available yet."}</p><ButtonPill label="Check again" onClick={onRetry} /></>;
                break;
            }
            case "membership_paused":
                content = <><h2>Membership Paused</h2><p>Your membership is paused. Resume any time to keep watching.</p>
                    <ButtonPill label="Resume Membership" link={RoutePaths.Account} /></>;
                break;
            case "membership_required":
                content = <><h2>Membership Required</h2><p>An active membership is required to watch this video.</p>
                    <ButtonPill label="Manage Membership" link={RoutePaths.Account} /></>;
                break;
            default:
                content = <><h2>Sign Up to Watch</h2><p>Unlock this video from {monthlyPrice ?? "our monthly membership rate"}.</p>
                    <ButtonPill label={monthlyOffer?.cta_label?.trim() || "Get Started"} link={withPromotionCode(RoutePaths.SignUp[0])} />
                    <ButtonPill label="Sign in" link={RoutePaths.Login} /></>;
        }
    }
    return <div className="watch-video__access-overlay"><div className="access-overlay__inner">
        <div className="access-overlay__content">{content}</div>
    </div></div>;
};
export default VideoAccessOverlay;
